import { deleteDoc, deleteField, getDoc, getDocs, limit, orderBy, query, serverTimestamp, setDoc, updateDoc, where, } from "firebase/firestore";
import { asTodoId, between, jitter, err, newId, notFound, ok, } from "@pack-it-up/shared";
import { emptyCompletion } from "@pack-it-up/core";
import { assignmentToDto } from "../mappers/common.js";
import { toDomainTodo, toTodoDto } from "../mappers/index.js";
import { observeQuery, patchField, tagsPatch, toDomainError } from "./support.js";
export const makeTodoRepo = (db, p) => {
    // listId equality filter is required for the read rule to be provable on a query
    // (rules key todos access on resource.data.listId).
    const appendOrder = async (listId, categoryId) => {
        const snap = await getDocs(query(p.todos(), where("listId", "==", listId), where("categoryId", "==", categoryId), orderBy("order", "desc"), limit(1)));
        const last = snap.docs[0]?.get("order");
        return jitter(between((last ?? null), null));
    };
    return {
        observeTodos: (listId) => observeQuery(query(p.todos(), where("listId", "==", listId)), toDomainTodo, "todos"),
        async createTodo(input) {
            try {
                const catSnap = await getDoc(p.category(input.categoryId));
                if (!catSnap.exists())
                    return err(notFound("category", input.categoryId));
                const listId = catSnap.get("parentId");
                const id = newId();
                const todo = {
                    id: asTodoId(id),
                    categoryId: input.categoryId,
                    name: input.name,
                    ...(input.description !== undefined ? { description: input.description } : {}),
                    ...(input.notes !== undefined ? { notes: input.notes } : {}),
                    ...(input.tags !== undefined && input.tags.length > 0 ? { tags: input.tags } : {}),
                    assignment: { kind: "everyone" },
                    completions: emptyCompletion(),
                    ...(input.dueDate !== undefined ? { dueDate: input.dueDate } : {}),
                    dueDateManuallyOverridden: false,
                    remindersSentTo: {},
                    reminderComplete: false,
                    order: await appendOrder(listId, input.categoryId),
                };
                await setDoc(p.todo(id), { ...toTodoDto(todo), listId });
                return ok(asTodoId(id));
            }
            catch (e) {
                return err(toDomainError(e));
            }
        },
        async updateTodo(patch) {
            const out = {};
            patchField(out, "name", patch.name);
            patchField(out, "description", patch.description);
            patchField(out, "notes", patch.notes);
            patchField(out, "tags", tagsPatch(patch.tags));
            patchField(out, "dueDate", patch.dueDate);
            patchField(out, "relativeDueOffset", patch.relativeDueOffset);
            // Nested field path: only the cached flag is written, the rest of the
            // provenance record is left exactly as the import/sync wrote it (Doc 02 §7).
            patchField(out, "provenance.modifiedSinceImport", patch.modifiedSinceImport);
            try {
                await updateDoc(p.todo(patch.todoId), out);
                return ok(undefined);
            }
            catch (e) {
                return err(toDomainError(e));
            }
        },
        async setCompletion(todoId, participantId, done) {
            try {
                const path = `completions.byParticipant.${participantId}`;
                await updateDoc(p.todo(todoId), done ? { [path]: { done: true, checkedBy: participantId, at: serverTimestamp() } } : { [path]: deleteField() });
                return ok(undefined);
            }
            catch (e) {
                return err(toDomainError(e));
            }
        },
        async setAssignment(todoId, assignment) {
            try {
                await updateDoc(p.todo(todoId), { assignment: assignmentToDto(assignment) });
                return ok(undefined);
            }
            catch (e) {
                return err(toDomainError(e));
            }
        },
        // Pinning a due date freezes recompute (Doc 09).
        async pinDueDate(todoId, dueDate) {
            try {
                await updateDoc(p.todo(todoId), { dueDate, dueDateManuallyOverridden: true });
                return ok(undefined);
            }
            catch (e) {
                return err(toDomainError(e));
            }
        },
        // `order` is the NEW (destination) fractional index in `toCategoryId`, not the
        // todo's current order — the caller computes it via `between(prev, next)`.
        async moveTodo(todoId, toCategoryId, order) {
            try {
                await updateDoc(p.todo(todoId), { categoryId: toCategoryId, order });
                return ok(undefined);
            }
            catch (e) {
                return err(toDomainError(e));
            }
        },
        async deleteTodo(todoId) {
            try {
                await deleteDoc(p.todo(todoId));
                return ok(undefined);
            }
            catch (e) {
                return err(toDomainError(e));
            }
        },
    };
};
//# sourceMappingURL=todo.repo.js.map